import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  BarChart3,
  DollarSign,
  Wallet,
  Calendar,
  Users,
  FileText,
  ClipboardList,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  Plus,
  Download,
  Search,
  Filter,
  Eye,
  Settings,
  Banknote,
  Target,
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Receipt,
  PiggyBank,
  WalletCards,
  Landmark,
  Handshake,
  Baby
} from 'lucide-react';
import GestionPaiementsEleves from './GestionPaiementsEleves';
import FacturationModal from './FacturationModal';
import SalaryManagementPage from './SalaryManagementPage';
import MaCaisse from './MaCaisse';
import NosDepenses from './NosDepenses';
import RecouvrementModule from './RecouvrementModule';
import { api } from '../services/api';
import { useFeatures } from '../contexts/FeaturesContext';
import { usePermissions } from '../hooks/usePermissions';
import { lireRecouvrementDisponible, noterRecouvrementDisponible } from '../utils/recouvrementDisponible';

const DASHBOARD_DATE_STORAGE_KEY = 'klasys_portefeuille_selected_date';
const ONGLET_STORAGE_KEY = 'klasys_comptabilite_onglet';
const COMPTABILITE_TABS = ['dashboard', 'paiements', 'paie', 'MaCaisse', 'depenses', 'recouvrement'];

// Le paiement des salaires est le seul onglet qui releve de la paie ; les cinq
// autres relevent de la comptabilite. Les deux se vendent separement : une ecole
// peut encaisser les scolarites, tenir sa caisse et son recouvrement sans jamais
// payer un salaire depuis KLASYS.
const ongletsPourLicence = (paieOuverte) =>
  COMPTABILITE_TABS.filter((onglet) => onglet !== 'paie' || paieOuverte);

// Deux onglets dépendent en plus de conditions lues après le premier rendu
// (décisions de Daniel, 19 septembre 2026) :
//   - « Caisse centrale » : dérogation COMPTABILITE_ET_PAIE.CAISSE_CENTRALE.VIEW ;
//   - « Recouvrement » : une année clôturée existe (sinon rien à recouvrer).
// Tant qu'une condition n'est pas connue, l'onglet n'est pas affiché.
const GRILLE_ONGLETS = { 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6' };

const ComptabiliteEtPaie = () => {
  const location = useLocation();
  const typeNavigation = useNavigationType();
  const { estActive } = useFeatures();
  const paieOuverte = estActive('module.paie');
  const { hasGranularPermission, loading: permissionsEnChargement } = usePermissions();
  const caisseCentraleOuverte = !permissionsEnChargement
    && hasGranularPermission('COMPTABILITE_ET_PAIE', 'CAISSE_CENTRALE', 'VIEW');
  // Connu dès le premier affichage (noté à la connexion, puis à chaque
  // vérification) : l'onglet ne surgit plus après coup. null seulement si le
  // navigateur n'a encore rien noté ; la vérification ci-dessous corrige et note.
  const [recouvrementDisponible, setRecouvrementDisponible] = useState(lireRecouvrementDisponible);
  useEffect(() => {
    let annule = false;
    api.anneesScolaires.getRecouvrementDisponible()
      .then((reponse) => {
        if (annule) return;
        // Une réponse en échec ne retire pas un onglet connu : seule une
        // réponse valide fait foi. Sans rien de noté, l'onglet reste absent.
        if (!reponse?.success) {
          setRecouvrementDisponible((actuel) => (actuel === null ? false : actuel));
          return;
        }
        const disponible = Boolean(reponse.data?.disponible);
        noterRecouvrementDisponible(disponible);
        setRecouvrementDisponible(disponible);
      })
      .catch(() => {
        if (!annule) setRecouvrementDisponible((actuel) => (actuel === null ? false : actuel));
      });
    return () => { annule = true; };
  }, []);
  const ongletsAutorises = useMemo(
    () => ongletsPourLicence(paieOuverte).filter((onglet) => (
      (onglet !== 'depenses' || caisseCentraleOuverte)
      && (onglet !== 'recouvrement' || recouvrementDisponible === true)
    )),
    [paieOuverte, caisseCentraleOuverte, recouvrementDisponible]
  );
  // Les conditions sont toutes connues : un onglet absent l'est pour de bon.
  const conditionsOngletsConnues = !permissionsEnChargement && recouvrementDisponible !== null;
  // États pour gérer les données et les interactions
  const [isLoading, setIsLoading] = useState(true);
  const [anneeActive, setAnneeActive] = useState(null);
  const [anneeActiveLoaded, setAnneeActiveLoaded] = useState(false);
  // L'onglet actif survit au démontage de l'écran, sans naviguer.
  //
  // Deux contraintes se contrariaient. `ProtectedRoute` démonte cet écran
  // pendant qu'il vérifie les permissions ; au remontage, l'état repart de sa
  // valeur initiale. Et l'ancienne version ne gardait l'onglet dans l'URL que
  // pour « recouvrement » : pour tous les autres, le paramètre était supprimé,
  // donc le remontage retombait sur « dashboard ».
  //
  // Écrire l'onglet dans l'URL réglait ça, mais provoquait une navigation à
  // CHAQUE clic — d'où le scintillement. La mémoire vit donc dans la session :
  // aucune navigation, et l'onglet est retrouvé au remontage.
  //
  // L'URL reste lue au premier montage, pour que les liens profonds existants
  // (`?tab=recouvrement`) continuent de fonctionner.
  //
  // Les liens directs du panneau des alertes critiques portent tous
  // `tab=paiements` : ils arrivent ici comme n'importe quel lien profond, et
  // les paiements font le reste (voir GestionPaiementsEleves).
  const [activeTab, setActiveTab] = useState(() => {
    const permis = ongletsPourLicence(estActive('module.paie'));
    const demande = new URLSearchParams(window.location.search).get('tab');
    if (permis.includes(demande)) return demande;
    const memorise = sessionStorage.getItem(ONGLET_STORAGE_KEY);
    return permis.includes(memorise) ? memorise : 'dashboard';
  });

  // L'instantane de licence peut arriver apres le premier rendu — le contexte
  // le lit d'abord dans localStorage, puis le rafraichit. Un onglet « paie »
  // memorise en session sous une ancienne licence doit alors ceder.
  useEffect(() => {
    if (!paieOuverte && activeTab === 'paie') setActiveTab('dashboard');
  }, [paieOuverte, activeTab]);

  // Même règle pour la caisse centrale et le recouvrement (lien profond ou
  // onglet mémorisé), une fois leurs conditions connues.
  useEffect(() => {
    if (conditionsOngletsConnues && !ongletsAutorises.includes(activeTab)) setActiveTab('dashboard');
  }, [conditionsOngletsConnues, ongletsAutorises, activeTab]);

  // Un lien profond ouvert pendant que l'écran est déjà là doit être suivi :
  // lien du panneau des alertes critiques, entrée « Comptabilité & Paie » du
  // menu, retour arrière. On suit chaque NOUVELLE navigation, reconnue à sa clé,
  // et non le changement de `tab` : l'adresse garde souvent l'onglet de la
  // navigation précédente (`tab=paiements` après une correction) pendant que
  // l'agent est passé sur un autre onglet, et un nouveau lien vers ce même
  // onglet laissait alors l'agent où il était. Un clic sur un onglet ne navigue
  // pas : il n'est jamais annulé par l'adresse restée en arrière.
  //
  // Un remplacement d'adresse n'est pas une demande de l'agent (les paiements
  // retirent un lien consommé, ou le réécrivent vers la famille) : on l'ignore.
  // Le montage, lui, est réglé par l'état initial.
  const derniereNavigation = useRef(location.key);
  useEffect(() => {
    if (location.key === derniereNavigation.current) return;
    derniereNavigation.current = location.key;
    if (typeNavigation === NavigationType.Replace) return;
    const demande = new URLSearchParams(location.search).get('tab');
    if (ongletsAutorises.includes(demande)) setActiveTab(demande);
  }, [location.key, location.search, typeNavigation, ongletsAutorises]);

  const handleTabChange = (nextTab) => {
    if (!ongletsAutorises.includes(nextTab)) return;
    setActiveTab(nextTab);
    try {
      sessionStorage.setItem(ONGLET_STORAGE_KEY, nextTab);
    } catch (e) {
      // Session verrouillée par le navigateur : l'onglet ne survivra pas au
      // remontage, mais l'écran continue de fonctionner.
    }
  };

  const emptyDashboardData = {
    soldeGeneral: { solde: 0, entrees: 0, sorties: 0 },
    moisActuel: { entrees: 0, sorties: 0, solde: 0 },
    moisScolaireCourant: { nom: null, entrees: 0, sorties: 0, solde: 0 },
    caisseDuJour: {
      encaissements: 0,
      dotationsOperationnelles: 0,
      sortiesPrincipales: 0,
      soldeDisponible: 0,
      statut: 'non_ouverte',
      estOuverte: false,
      estCloturee: false
    },
    budgetDepenses: {
      dotationsBudget: 0,
      depensesFixes: 0,
      depensesVariables: 0,
      depensesUtilisees: 0,
      disponible: 0
    },
    paiementsScolarite: { nbElevesServis: 0, nbPaiements: 0, totalEncaisse: 0 },
    portefeuille: { solde: 0, nom: '' },
    indicateurs: {
      tauxPaiementFraisScolaires: 0,
      tauxPaiementSalaires: 0,
      niveauDepenses: 0,
      progressionRevenus: 0
    },
    evolution: [],
    tauxPaiement: [],
    transactionsRecentes: []
  };

  const [dashboardData, setDashboardData] = useState(emptyDashboardData);

  const formatDateString = (date) => {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  };

  const getLocalDateString = () => formatDateString(new Date());

  // Le tableau de bord se cale sur aujourd'hui, toujours.
  //
  // Il lisait la date mémorisée par le portefeuille, qui l'écrit à chaque fois
  // qu'on y navigue dans le passé. Résultat : après avoir consulté le 12 mars
  // dans le portefeuille, la carte « Caisse du jour » affichait l'état du
  // 12 mars — et annonçait « Ouverte » alors que la caisse d'aujourd'hui ne
  // l'était pas. Un tableau de bord répond à « où en suis-je maintenant » : le
  // faire basculer sur une date passée répond à une question que personne n'a
  // posée.
  const getDashboardDateString = () => getLocalDateString();

  const buildPortefeuilleParams = (date) => ({
    periode: 'jour',
    annee_scolaire_id: anneeActive?.id,
    date_debut: date,
    date_fin: date
  });

  // État pour le refresh



  useEffect(() => {
    const fetchAnneeActive = async () => {
      try {
        const response = await api.anneesScolaires.getActive();
        if (response.success && response.data) {
          setAnneeActive(response.data);
        }
      } catch (error) {
        console.error("Erreur lors de la recuperation de l'annee scolaire active:", error);
      } finally {
        setAnneeActiveLoaded(true);
      }
    };

    fetchAnneeActive();
  }, []);

  const fetchDashboardData = useCallback(async () => {
    if (!anneeActiveLoaded || !anneeActive?.id) return;
    try {
      setIsLoading(true);

      console.log('📊 Chargement des stats du dashboard...');

      const dashboardDate = getDashboardDateString();
      const response = await api.comptabilite.getDashboardStatsPersonnel(anneeActive?.id, dashboardDate);
      const effectiveDashboardDate = response?.data?.caisseDuJour?.date || dashboardDate;
      const portefeuilleParams = buildPortefeuilleParams(effectiveDashboardDate);

      const [statutResponse, statsResponse, statsDetailleesResponse] = await Promise.all([
        api.portefeuille.getStatutCloture(effectiveDashboardDate, anneeActive?.id),
        api.portefeuille.getStatsFiltrees(portefeuilleParams),
        api.portefeuille.getStatsDetaillees(portefeuilleParams)
      ]);

      console.log('Dashboard backend response:', response);

      if (response.success) {
        if (response.data.aucunPortefeuille) {
          // Cas où l'utilisateur n'a pas de portefeuille
          console.log('⚠️ Aucun portefeuille assigné');
          setDashboardData({
            aucunPortefeuille: true,
            message: response.data.message,
            soldeGeneral: { solde: 0, entrees: 0, sorties: 0 },
            moisActuel: { entrees: 0, sorties: 0, solde: 0 },
            paiementsScolarite: { nbPaiements: 0, totalEncaisse: 0 },
            portefeuille: { solde: 0, nom: 'Non assigné' },
            evolution: [],
            transactionsRecentes: []
          });
        } else {
          const statsPortefeuille = statsResponse?.success ? statsResponse.data : null;
          const statsDetaillees = statsDetailleesResponse?.success ? statsDetailleesResponse.data : null;
          const statutCaisse = statutResponse?.success ? statutResponse.data : null;

          const caisseDuJour = {
            ...(response.data.caisseDuJour || emptyDashboardData.caisseDuJour),
            date: effectiveDashboardDate,
            id: statutCaisse?.caisse_journaliere_id || response.data.caisseDuJour?.id || null,
            statut: statutCaisse?.statut || response.data.caisseDuJour?.statut || 'non_ouverte',
            estOuverte: Boolean(statutCaisse?.est_ouverte),
            estCloturee: Boolean(statutCaisse?.est_cloturee),
            // Marque que ces deux valeurs viennent du serveur, et non de la
            // valeur par défaut.
            statutConnu: Boolean(statutCaisse),
            ouverteLe: statutCaisse?.date_ouverture || response.data.caisseDuJour?.ouverteLe || null,
            clotureeLe: statutCaisse?.date_cloture || response.data.caisseDuJour?.clotureeLe || null,
            encaissements: Number(statsPortefeuille?.totalEncaisse || 0),
            dotationsOperationnelles: Number(statsPortefeuille?.dotationsOperationnelles ?? statsPortefeuille?.totalDotations ?? 0),
            sortiesPrincipales: Number(statsPortefeuille?.totalDepense || 0),
            soldeDisponible: Number(statsPortefeuille?.soldeCaissePrincipale ?? statsPortefeuille?.caisseActuelle ?? response.data.caisseDuJour?.soldeDisponible ?? 0)
          };

          const budgetDepenses = {
            ...(response.data.budgetDepenses || emptyDashboardData.budgetDepenses),
            dotationsBudget: Number(statsDetaillees?.dotationsBudgetDepenses ?? statsPortefeuille?.dotationsBudgetDepenses ?? 0),
            depensesFixes: Number(statsDetaillees?.depensesBudgetFixes || 0),
            depensesVariables: Number(statsDetaillees?.depensesBudgetVariables || 0),
            depensesUtilisees: Number(statsDetaillees?.depensesBudgetTotal ?? statsPortefeuille?.depensesBudgetTotal ?? 0),
            disponible: Number(statsDetaillees?.soldeBudgetDepenses ?? statsPortefeuille?.soldeBudgetDepenses ?? response.data.budgetDepenses?.disponible ?? 0)
          };

          console.log('Dashboard data loaded:', response.data);
          setDashboardData({
            ...response.data,
            caisseDuJour,
            budgetDepenses
          });
        }
      } else {
        console.error('❌ Erreur:', response.message);
        // Initialiser avec des valeurs par défaut en cas d'erreur
        setDashboardData({
          soldeGeneral: { solde: 0, entrees: 0, sorties: 0 },
          moisActuel: { entrees: 0, sorties: 0, solde: 0 },
          paiementsScolarite: { nbPaiements: 0, totalEncaisse: 0 },
          portefeuille: { solde: 0, nom: 'Erreur' },
          evolution: [],
          transactionsRecentes: []
        });
      }

      setIsLoading(false);
    } catch (error) {
      console.error("❌ Erreur chargement dashboard:", error);
      // Initialiser avec des valeurs par défaut en cas d'erreur
      setDashboardData({
        soldeGeneral: { solde: 0, entrees: 0, sorties: 0 },
        moisActuel: { entrees: 0, sorties: 0, solde: 0 },
        paiementsScolarite: { nbPaiements: 0, totalEncaisse: 0 },
        portefeuille: { solde: 0, nom: 'Erreur' },
        evolution: [],
        transactionsRecentes: []
      });
      setIsLoading(false);
    }
  }, [anneeActiveLoaded, anneeActive?.id]);

  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    fetchDashboardData();
  }, [activeTab, fetchDashboardData]);






  // Fonction pour formater les montants (arrondir car FCFA n'a pas de centimes)
  const formatMontant = (montant) => {
    const valeur = Number.isFinite(Number(montant)) ? Number(montant) : 0;
    return Math.round(valeur).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const formatSignedMontant = (montant) => {
    const valeur = Number.isFinite(Number(montant)) ? Number(montant) : 0;
    const signe = valeur >= 0 ? '+' : '-';
    return signe + formatMontant(Math.abs(valeur)) + ' FCFA';
  };

  const formatDateDisplay = (value) => {
    if (!value) return null;
    const normalized = String(value).slice(0, 10);
    const [year, month, day] = normalized.split('-');
    return year && month && day ? day + '/' + month + '/' + year : normalized;
  };

  const caisseJour = dashboardData.caisseDuJour || emptyDashboardData.caisseDuJour;
  // On ne sait rien tant que le tableau de bord n'a pas répondu au moins une
  // fois : `statutConnu` n'est posé qu'après une réponse effectivement reçue.
  const caisseInconnue = isLoading || !caisseJour.statutConnu;

  // La caisse affichée n'est pas toujours celle d'aujourd'hui.
  //
  // Quand aucune caisse n'existe pour la journée en cours, le serveur retourne
  // la dernière caisse restée ouverte et le signale par `dateSource`. L'écran
  // ignorait ce drapeau : il annonçait « Caisse du jour — Ouverte » en montrant
  // une caisse du 5 août. Or une caisse d'un jour passé encore ouverte n'est pas
  // une bonne nouvelle : c'est ce qui EMPÊCHE d'ouvrir celle d'aujourd'hui. Le
  // portefeuille le dit déjà — « Vous devez la clôturer avant d'ouvrir ou
  // d'utiliser la caisse du jour » — le tableau de bord doit dire la même chose.
  const caisseAncienneNonCloturee = !caisseInconnue
    && caisseJour.dateSource === 'derniere_caisse_ouverte';
  const caisseDateLabel = formatDateDisplay(caisseJour.date);
  const budgetDepenses = dashboardData.budgetDepenses || emptyDashboardData.budgetDepenses;
  const moisCourant = dashboardData.moisScolaireCourant || dashboardData.moisActuel || emptyDashboardData.moisScolaireCourant;
  // Aucun mois scolaire ne couvre la date consultée — courant en août, l'année
  // allant d'octobre à juin. La carte le dit plutôt que d'annoncer « Mois
  // courant » sur des chiffres qui n'appartiennent à aucun mois.
  const horsPeriodeScolaire = Boolean(dashboardData.horsPeriodeScolaire);
  const libelleMoisCourant = moisCourant?.nom
    ? (horsPeriodeScolaire ? `${moisCourant.nom} · hors calendrier scolaire` : moisCourant.nom)
    : 'Mois courant';
  const totalSortiesMoisCourant = Number(moisCourant?.sorties || 0);
  // Quatre états, pas trois : « je ne sais pas encore » n'est pas « ce n'est pas
  // ouvert ». Les deux se confondaient, si bien qu'un écran en cours de
  // chargement annonçait une caisse fermée à quelqu'un dont la caisse était
  // ouverte — l'incohérence disparaissait une seconde plus tard, ce qui la
  // rendait d'autant plus déroutante.
  const getCaisseStatus = () => {
    if (caisseInconnue) return { label: 'Vérification…', className: 'bg-slate-100 text-slate-500 border-slate-200' };
    if (caisseAncienneNonCloturee) return { label: 'Non clôturée', className: 'bg-amber-100 text-amber-800 border-amber-300' };
    if (caisseJour.estCloturee) return { label: 'Clôturée', className: 'bg-slate-100 text-slate-700 border-slate-200' };
    if (caisseJour.estOuverte) return { label: 'Ouverte', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
    return { label: 'Non ouverte', className: 'bg-amber-100 text-amber-700 border-amber-200' };
  };

  const caisseStatus = getCaisseStatus();

  return (
    <div className="w-full p-4">
      <h1 className="text-3xl font-bold mb-6 text-[#246990]">Comptabilité & Paie</h1>

      {/* Navigation par onglets */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className={`grid ${GRILLE_ONGLETS[ongletsAutorises.length] || 'grid-cols-6'} mb-8 bg-[#319190]/10 `}>
          <TabsTrigger value="dashboard" className="flex items-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white">
            <BarChart3 size={16} />
            <span className="hidden md:inline">Tableau de bord</span>
          </TabsTrigger>
          <TabsTrigger value="paiements" className="flex items-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white">
            <Baby size={16} />
            <span className="hidden md:inline">Paiements scolarités</span>
          </TabsTrigger>
          {paieOuverte && (
            <TabsTrigger value="paie" className="flex items-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white">
              <Users size={16} />
              <span className="hidden md:inline">Paiement des salaires</span>
            </TabsTrigger>
          )}

          <TabsTrigger value="MaCaisse" className="flex items-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white">
            <WalletCards size={16} />
            <span className="hidden md:inline">Portefeuille</span>
          </TabsTrigger>

          {ongletsAutorises.includes('depenses') && (
            <TabsTrigger value="depenses" className="flex items-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white">
              <Landmark size={16} />
              <span className="hidden md:inline">Caisse centrale</span>
            </TabsTrigger>
          )}
          {ongletsAutorises.includes('recouvrement') && (
            <TabsTrigger value="recouvrement" className="flex items-center gap-2 data-[state=active]:bg-[#319190] data-[state=active]:text-white">
              <Handshake size={16} />
              <span className="hidden md:inline">Recouvrement</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Contenu du tableau de bord */}
        <TabsContent value="dashboard">
          {/* Navigation de période - directement sur le fond de page */}



          {/* Cartes du haut */}
          {/* Deux cartes seulement : la caisse du jour et le budget depenses.
              Les deux cartes mensuelles sont parties au portefeuille, ou
              elles disposent de bornes justes et d'une ventilation. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card className="border border-[#246990]/20 bg-white shadow-sm">
              <CardContent className="p-5">
                {/* En-tête sur deux lignes plutôt qu'une.
                    La carte occupe un quart de la grille : y aligner icône,
                    titre, sous-titre ET badge sur une seule rangée réduisait le
                    texte à une colonne de quarante pixels, où la moindre phrase
                    se brisait sur six lignes. Le badge partage la première ligne
                    avec le titre ; l'explication prend la largeur entière. */}
                <div className="mb-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="p-2 rounded-lg bg-[#246990]/10 text-[#246990] flex-shrink-0">
                        <Wallet className="w-5 h-5" />
                      </span>
                      <p className="text-sm font-semibold text-[#246990] truncate">
                        {caisseAncienneNonCloturee ? 'Caisse non clôturée' : 'Caisse du jour'}
                      </p>
                    </div>
                    {/* Badge isolé : largeur libre, mais insécable. La charte
                        réserve la largeur fixe aux badges qui se répètent d'une
                        ligne à l'autre dans un tableau. */}
                    <span className={"inline-flex items-center h-6 px-3 rounded-full border text-xs font-medium whitespace-nowrap flex-shrink-0 " + caisseStatus.className}>
                      {caisseStatus.label}
                    </span>
                  </div>
                  {/* Le sous-titre affirmait « Solde disponible au 05/08/2026 » AVANT
                      de savoir de quelle caisse il parlait — la date comprise, héritée
                      du rendu précédent. Tant qu'on ne sait pas, on n'annonce ni date
                      ni solde. */}
                  <p className="text-xs text-slate-500 leading-snug">
                    {caisseInconnue
                      ? "Lecture de l'état de la caisse…"
                      : caisseAncienneNonCloturee
                        ? `Celle du ${caisseDateLabel} est encore ouverte. À clôturer avant d'utiliser la caisse d'aujourd'hui.`
                        : `Solde disponible${caisseDateLabel ? ` au ${caisseDateLabel}` : " aujourd'hui"}`}
                  </p>
                </div>
                {isLoading ? (
                  <div className="h-28 bg-slate-100 rounded animate-pulse" />
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-baseline justify-between gap-3"><span className="text-slate-600 min-w-0 truncate">Encaissements</span><span className="font-semibold tabular-nums whitespace-nowrap flex-shrink-0 text-emerald-700">{formatSignedMontant(caisseJour.encaissements)}</span></div>
                      <div className="flex items-baseline justify-between gap-3"><span className="text-slate-600 min-w-0 truncate">Dotations opérationnelles</span><span className="font-semibold tabular-nums whitespace-nowrap flex-shrink-0 text-blue-700">{formatSignedMontant(caisseJour.dotationsOperationnelles)}</span></div>
                      <div className="flex items-baseline justify-between gap-3"><span className="text-slate-600 min-w-0 truncate">Sorties principales</span><span className="font-semibold tabular-nums whitespace-nowrap flex-shrink-0 text-red-700">-{formatMontant(caisseJour.sortiesPrincipales || 0)} FCFA</span></div>
                    </div>
                    <div className="pt-3 border-t border-slate-200 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                      <span className="text-sm font-semibold text-slate-700">Solde disponible</span>
                      {/* `text-xl` et non `2xl` : dans une carte au quart de la
                          grille, un montant à six chiffres remplissait la ligne
                          entière et repoussait son libellé. */}
                      <span className="text-xl font-bold tabular-nums whitespace-nowrap text-[#246990]">
                        {formatMontant(caisseJour.soldeDisponible || 0)} FCFA
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border border-amber-200 bg-amber-50/60 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
                      <PiggyBank className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Budget dépenses</p>
                      <p className="text-xs text-amber-700/80">Budget séparé des encaissements</p>
                    </div>
                  </div>
                </div>
                {isLoading ? (
                  <div className="h-28 bg-amber-100/70 rounded animate-pulse" />
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-baseline justify-between gap-3"><span className="text-slate-700 min-w-0 truncate">Dotations budget reçues</span><span className="font-semibold tabular-nums whitespace-nowrap flex-shrink-0 text-blue-700">{formatSignedMontant(budgetDepenses.dotationsBudget)}</span></div>
                      <div className="flex items-baseline justify-between gap-3"><span className="text-slate-700 min-w-0 truncate">Dépenses fixes</span><span className="font-semibold text-orange-700">-{formatMontant(budgetDepenses.depensesFixes || 0)} FCFA</span></div>
                      <div className="flex items-baseline justify-between gap-3"><span className="text-slate-700 min-w-0 truncate">Dépenses variables</span><span className="font-semibold text-orange-700">-{formatMontant(budgetDepenses.depensesVariables || 0)} FCFA</span></div>
                    </div>
                    <div className="pt-3 border-t border-amber-200 flex items-end justify-between">
                      <span className="text-sm font-semibold text-slate-700">Budget disponible</span>
                      <span className="text-2xl font-bold text-amber-700">{formatMontant(budgetDepenses.disponible || 0)} FCFA</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

          </div>

          {/* Indicateurs circulaires.
              Sans la paie il n'y en a que trois : la grille suit, sinon la
              quatrieme colonne resterait beante a droite du dernier cadran. */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${paieOuverte ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-6`}>
            {[
              {
                titre: 'Frais scolaires',
                pourcentage: dashboardData.indicateurs?.tauxPaiementFraisScolaires || 0,
                statut: (dashboardData.indicateurs?.tauxPaiementFraisScolaires || 0) >= 80 ? 'success' : (dashboardData.indicateurs?.tauxPaiementFraisScolaires || 0) >= 50 ? 'warning' : 'danger',
                description: (dashboardData.indicateurs?.tauxPaiementFraisScolaires || 0) >= 80 ? 'Paiements à jour !' : (dashboardData.indicateurs?.tauxPaiementFraisScolaires || 0) >= 50 ? 'Quelques retards' : 'Beaucoup de retards',
                tooltip: 'Pourcentage d\'élèves ayant payé leurs frais ce mois. Calcul : (Nombre d\'élèves payés / Nombre total d\'élèves) × 100. Données 100% réelles du mois en cours.'
              },
              // Sans la paie, ce taux vaut zero et se lirait comme un retard de
              // paiement dans une ecole qui ne paie aucun salaire ici.
              ...(paieOuverte ? [{
                titre: 'Paiement salaires',
                pourcentage: dashboardData.indicateurs?.tauxPaiementSalaires || 0,
                statut: (dashboardData.indicateurs?.tauxPaiementSalaires || 0) >= 80 ? 'success' : (dashboardData.indicateurs?.tauxPaiementSalaires || 0) >= 50 ? 'warning' : 'danger',
                description: (dashboardData.indicateurs?.tauxPaiementSalaires || 0) >= 80 ? 'Salaires à jour !' : 'Des salaires restent à payer',
                tooltip: 'Pourcentage de salaires finalisés (statut "clôturé") ce mois. Calcul : (Nombre de salaires clôturés / Nombre total de salaires) × 100. Note : Statut "payé" = enregistré, "clôturé" = finalisé et verrouillé.'
              }] : []),
              {
                titre: 'Budget dépenses',
                pourcentage: dashboardData.indicateurs?.niveauDepenses || 0,
                statut: (dashboardData.indicateurs?.niveauDepenses || 0) <= 70 ? 'success' : (dashboardData.indicateurs?.niveauDepenses || 0) <= 90 ? 'warning' : 'danger',
                description: (dashboardData.indicateurs?.niveauDepenses || 0) <= 70 ? 'Budget maîtrisé' : (dashboardData.indicateurs?.niveauDepenses || 0) <= 90 ? 'Budget à surveiller' : 'Budget presque consommé',
                tooltip: 'Pourcentage du budget dépenses déjà utilisé. Calcul : (Dépenses budget utilisées / Dotations budget reçues) × 100. Les encaissements scolaires ne sont pas mélangés avec ce budget.'
              },
              {
                titre: 'Progression revenus',
                pourcentage: dashboardData.indicateurs?.progressionRevenus || 0,
                statut: (dashboardData.indicateurs?.progressionRevenus || 0) >= 80 ? 'success' : (dashboardData.indicateurs?.progressionRevenus || 0) >= 50 ? 'warning' : 'danger',
                description: (dashboardData.indicateurs?.progressionRevenus || 0) >= 80 ? 'Excellent rythme !' : (dashboardData.indicateurs?.progressionRevenus || 0) >= 50 ? 'À mi-parcours' : 'Besoin d\'accélérer',
                tooltip: 'Avancement vers l\'objectif annuel de revenus. Calcul : (Montant total encaissé / Objectif annuel total) × 100. L\'objectif annuel est la somme de tous les frais de scolarité attendus pour l\'année scolaire.'
              }
            ].map((indicateur, index) => {
              const couleur = indicateur.statut === 'success' ? '#10b981' : indicateur.statut === 'warning' ? '#f59e0b' : '#ef4444';
              const bgCouleur = indicateur.statut === 'success' ? 'bg-green-50' : indicateur.statut === 'warning' ? 'bg-orange-50' : 'bg-red-50';
              const textCouleur = indicateur.statut === 'success' ? 'text-green-700' : indicateur.statut === 'warning' ? 'text-orange-700' : 'text-red-700';
              const borderCouleur = indicateur.statut === 'success' ? 'border-green-200' : indicateur.statut === 'warning' ? 'border-orange-200' : 'border-red-200';

              return (
                <Card key={index} className={`${bgCouleur} border-0 shadow-md hover:shadow-lg transition-all`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center relative group">
                      {/* Cercle SVG */}
                      <div className="relative inline-flex mb-4">
                        <svg className="w-32 h-32 transform -rotate-90">
                          <circle cx="64" cy="64" r="56" stroke="#e5e7eb" strokeWidth="10" fill="none" />
                          <circle
                            cx="64" cy="64" r="56"
                            stroke={couleur}
                            strokeWidth="10"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 56}`}
                            strokeDashoffset={`${2 * Math.PI * 56 * (1 - indicateur.pourcentage / 100)}`}
                            strokeLinecap="round"
                            className="transition-all duration-1000"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className={`text-3xl font-bold ${textCouleur}`}>{indicateur.pourcentage}%</span>
                        </div>
                      </div>

                      {/* Titre avec tooltip */}
                      <div className="flex items-center gap-1 mb-2">
                        <h3 className={`text-sm font-semibold ${textCouleur} text-center`}>{indicateur.titre}</h3>
                        <AlertCircle className={`w-4 h-4 ${textCouleur} opacity-60 cursor-help`} />

                        {/* Tooltip amélioré */}
                        <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full mt-2 w-80 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-4 hidden group-hover:block z-50 border-2 ${borderCouleur}`}>
                          <div className="flex items-start gap-2">
                            <AlertCircle className={`w-5 h-5 ${textCouleur} flex-shrink-0 mt-0.5`} />
                            <div>
                              <p className={`font-bold ${textCouleur} mb-2 text-sm`}>{indicateur.titre}</p>
                              <p className="text-gray-700 leading-relaxed">{indicateur.tooltip}</p>
                            </div>
                          </div>
                          <div className={`absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-b-2 border-r-2 ${borderCouleur} rotate-45`}></div>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-gray-600 text-center">{indicateur.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>


          {/* Graphique 1 : Revenus vs Dépenses */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#246990]">Revenus vs dépenses (année scolaire)</CardTitle>
              <CardDescription>
                Flux mensuels de <span className="font-semibold">votre caisse</span> — pas ceux de
                l'établissement. Les mois hors calendrier scolaire, s'il y en a, figurent à la suite,
                marqués en ambre.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-pulse text-gray-400">Chargement...</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Graphique en barres */}
                  <div className="flex items-end justify-between gap-2 h-56">
                    {dashboardData.evolution && dashboardData.evolution.length > 0 ? (
                      dashboardData.evolution.map((data, index) => {
                        const maxVal = Math.max(...dashboardData.evolution.map(d => Math.max(d.entrees || 0, d.sorties || 0)));
                        const hauteurRevenus = maxVal > 0 ? ((data.entrees || 0) / maxVal) * 100 : 0;
                        const hauteurDepenses = maxVal > 0 ? ((data.sorties || 0) / maxVal) * 100 : 0;

                        const moisNoms = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

                        return (
                          <div key={index} className="flex-1 flex flex-col items-center gap-2 group">
                            {/* Tooltip */}

                            {/* Tooltip stylisé avec tes couleurs */}
                            <div className="absolute -mt-32 hidden group-hover:flex flex-col bg-white text-gray-800 text-xs rounded-lg shadow-2xl border-2 border-[#319190] px-4 py-3 whitespace-nowrap z-10">
                              {/* En-tête */}
                              <div className="flex items-center gap-2 pb-2 mb-2 border-b border-[#319190]/30">
                                <Calendar className="w-4 h-4 text-[#319190]" />
                                <span className="font-bold text-sm text-[#319190]">{moisNoms[data.mois - 1]} {data.annee}</span>
                                {data.horsCalendrier && (
                                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800">
                                    Hors calendrier
                                  </span>
                                )}
                              </div>

                              {/* Revenus */}
                              <div className="flex items-center justify-between gap-6 py-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                                  <span className="text-gray-600">Revenus:</span>
                                </div>
                                <span className="font-bold text-green-700">{formatMontant(data.entrees || 0)} FCFA</span>
                              </div>

                              {/* Dépenses */}
                              <div className="flex items-center justify-between gap-6 py-1">
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 bg-[#c66d59] rounded"></div>
                                  <span className="text-gray-600">Dépenses:</span>
                                </div>
                                <span className="font-bold text-[#c66d59]">{formatMontant(data.sorties || 0)} FCFA</span>
                              </div>

                              {/* Solde */}
                              <div className="flex items-center justify-between gap-6 pt-2 mt-2 border-t border-[#319190]/30">
                                <div className="flex items-center gap-2">
                                  <TrendingUp className="w-4 h-4 text-[#319190]" />
                                  <span className="text-gray-700 font-semibold">Solde:</span>
                                </div>

                                <span className={`font-bold text-sm ${(data.entrees || 0) - (data.sorties || 0) >= 0 ? 'text-[#319190]' : 'text-[#c66d59]'}`}>
                                  {formatMontant(Math.round((data.entrees || 0) - (data.sorties || 0)))} FCFA
                                </span>

                              </div>

                              {/* Flèche du tooltip */}
                              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-b-2 border-r-2 border-[#319190] rotate-45"></div>
                            </div>




                            {/* Barres */}
                            {/* Liseré ambre : ce mois n'appartient a aucun mois
                                scolaire. Il compte, mais il ne se lit pas comme
                                les autres. */}
                            <div className={`flex gap-1 items-end h-48 w-full ${data.horsCalendrier
                              ? 'rounded-t border-x border-t border-dashed border-amber-300 bg-amber-50/40 px-0.5'
                              : ''}`}>
                              <div
                                className="flex-1 bg-[#319190] rounded-t hover:bg-[#277372] transition-colors"
                                style={{ height: `${hauteurRevenus}%` }}
                              />
                              <div
                                className="flex-1 bg-[#c66d59] rounded-t hover:bg-[#a75849] transition-colors"
                                style={{ height: `${hauteurDepenses}%` }}
                              />
                            </div>


                            {/* Label mois */}
                            <span className={`text-xs font-medium ${data.horsCalendrier ? 'text-amber-700' : 'text-gray-600'}`}>{moisNoms[data.mois - 1]}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="w-full h-48 flex items-center justify-center text-gray-400">
                        Aucune donnée disponible
                      </div>
                    )}
                  </div>

                  {/* Légende */}
                  <div className="flex items-center justify-center gap-6 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-[#319190] rounded"></div>
                      <span className="text-sm text-gray-600">Revenus</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-[#c66d59] rounded"></div>
                      <span className="text-sm text-gray-600">Dépenses</span>
                    </div>
                  </div>

                </div>
              )}
            </CardContent>
          </Card>

          {/* Graphique 2 : Évolution taux de paiement */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[#246990]">Évolution taux de paiement</CardTitle>
              <CardDescription>Taux réel de paiement des frais de scolarité sur l'année scolaire</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {isLoading ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-pulse text-gray-400">Chargement...</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Graphique en barres colorées */}
                  <div className="flex items-end justify-between gap-2 h-56">
                    {dashboardData.tauxPaiement && dashboardData.tauxPaiement.length > 0 ? (
                      dashboardData.tauxPaiement.map((data, index) => {
                        const taux = data.tauxMontant; // Taux basé sur les montants réels
                        const hauteur = Math.min(taux, 100); // Limiter à 100%

                        // Couleurs selon le taux
                        const couleur = taux >= 80 ? 'bg-green-500 hover:bg-green-600' :
                          taux >= 50 ? 'bg-orange-500 hover:bg-orange-600' :
                            'bg-red-500 hover:bg-red-600';

                        const moisNoms = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

                        return (
                          <div key={index} className="flex-1 flex flex-col items-center gap-2 group">
                            {/* Tooltip détaillé */}

                            {/* Tooltip stylisé avec tes couleurs */}
                            {/* Tooltip stylisé avec tes couleurs */}
                            {/* Tooltip stylisé avec tes couleurs */}
                            {/* Tooltip stylisé avec tes couleurs */}
                            <div className="absolute -mt-40 hidden group-hover:flex flex-col bg-white text-gray-800 text-[11px] rounded-lg shadow-2xl border-2 border-[#319190] px-3 py-2 whitespace-nowrap z-[9999]">
                              {/* En-tête */}
                              <div className="flex items-center gap-1.5 pb-1.5 mb-1.5 border-b border-[#319190]/30">
                                <Calendar className="w-3 h-3 text-[#319190]" />
                                <span className="font-bold text-xs text-[#319190]">
                                  {moisNoms[data.mois - 1]} {data.annee}
                                </span>
                              </div>

                              {/* Montant attendu */}
                              <div className="flex items-center justify-between gap-4 py-0.5">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2.5 h-2.5 bg-[#319190] rounded"></div>
                                  <span className="text-gray-600">Attendu:</span>
                                </div>
                                <span className="font-bold text-[#319190]">{formatMontant(data.montantAttendu)} FCFA</span>
                              </div>

                              {/* Montant payé */}
                              <div className="flex items-center justify-between gap-4 py-0.5">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2.5 h-2.5 bg-green-500 rounded"></div>
                                  <span className="text-gray-600">Payé:</span>
                                </div>
                                <span className="font-bold text-green-700">{formatMontant(data.montantPaye)} FCFA</span>
                              </div>

                              {/* 🆕 Reste à recouvrir */}
                              <div className="flex items-center justify-between gap-4 py-0.5">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-2.5 h-2.5 bg-[#c66d59] rounded"></div>
                                  <span className="text-gray-600">Reste à recouvrir:</span>
                                </div>
                                <span className="font-bold text-[#c66d59]">
                                  {formatMontant(Math.max(0, data.montantAttendu - data.montantPaye))} FCFA
                                </span>
                              </div>

                              {/* Taux de paiement */}
                              <div className="flex items-center justify-between gap-4 pt-1.5 mt-1.5 border-t border-[#319190]/30">
                                <div className="flex items-center gap-1.5">
                                  <TrendingUp className="w-3 h-3 text-[#319190]" />
                                  <span className="text-gray-700 font-semibold">Taux:</span>
                                </div>
                                <span className={`font-bold text-sm ${data.tauxMontant >= 80 ? 'text-green-600' :
                                  data.tauxMontant >= 50 ? 'text-orange-600' :
                                    'text-[#c66d59]'
                                  }`}>
                                  {data.tauxMontant}%
                                </span>
                              </div>

                              {/* Nombre de paiements */}
                              <div className="pt-1.5 mt-1.5 border-t border-gray-100">
                                <span className="text-gray-500 text-[10px] flex items-center justify-center gap-1">
                                  <CreditCard className="w-2.5 h-2.5" />
                                  {data.nbPayes}/{data.nbPaiements} paiements effectués
                                </span>
                              </div>

                              {/* Flèche du tooltip */}
                              <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white border-b-2 border-r-2 border-[#319190] rotate-45"></div>
                            </div>

                            {/* Barre */}
                            <div className="flex items-end h-48 w-full">
                              <div
                                className={`w-full ${couleur} rounded-t transition-all relative shadow-md`}
                                style={{ height: `${hauteur}%` }}
                              >
                                {/* Afficher le % sur la barre si > 15% */}
                                {hauteur > 15 && (
                                  <div className="absolute top-2 w-full text-center">
                                    <span className="text-white text-xs font-bold drop-shadow-lg">
                                      {data.tauxMontant}%
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Label mois */}
                            <span className="text-xs text-gray-700 font-semibold">{moisNoms[data.mois - 1]}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="w-full h-48 flex flex-col items-center justify-center text-gray-400">
                        <svg className="w-16 h-16 mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <p className="text-sm">Aucune donnée disponible</p>
                        <p className="text-xs mt-1">Les taux de paiement s'afficheront ici</p>
                      </div>
                    )}
                  </div>

                  {/* Légende avec statistiques */}
                  <div className="pt-4 border-t space-y-3">
                    <div className="flex items-center justify-center gap-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-[#319190] rounded shadow-sm"></div>
                        <span className="text-gray-600 font-medium">≥80% (Excellent)</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-orange-500 rounded shadow-sm"></div>
                        <span className="text-gray-600 font-medium">50-79% (Moyen)</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 bg-[#c66d59] rounded shadow-sm"></div>
                        <span className="text-gray-600 font-medium">&lt;50% (Faible)</span>
                      </div>
                    </div>



                  </div>
                </div>
              )}
            </CardContent>
          </Card>

        </TabsContent>

        {/* Contenu de l'onglet Paiements Élèves */}
        <TabsContent value="paiements">
          <GestionPaiementsEleves />
        </TabsContent>

        {/* Ajouter ici les contenus des autres onglets */}
        <TabsContent value="facturation">
          <FacturationModal />
        </TabsContent>

        {ongletsAutorises.includes('depenses') && (
          <TabsContent value="depenses">
            <NosDepenses />
          </TabsContent>
        )}

        {ongletsAutorises.includes('recouvrement') && (
          <TabsContent value="recouvrement">
            <RecouvrementModule />
          </TabsContent>
        )}

        {paieOuverte && (
          <TabsContent value="paie">
            <SalaryManagementPage />
          </TabsContent>
        )}

        <TabsContent value="MaCaisse">
          <MaCaisse />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ComptabiliteEtPaie;
